import { TransferError, TransferFailure, TransferFile } from "../file-transfer";
import { md5Hex } from "../md5";
import type { ChargePointSimulator } from "../simulator";

/** Deterministic pseudo hash (hex) for simulated certificate hash data; not cryptographic. */
export const pseudoHash = (input: string, length = 64): string => {
  let out = "";
  let seed = 0x811c9dc5;
  while (out.length < length) {
    for (let i = 0; i < input.length; i++) {
      seed ^= input.charCodeAt(i) + out.length;
      seed = Math.imul(seed, 0x01000193) >>> 0;
    }
    out += seed.toString(16).padStart(8, "0");
  }
  return out.slice(0, length);
};

export const certificateHashData = (certificate: string) => ({
  hashAlgorithm: "SHA256",
  issuerNameHash: pseudoHash(`issuer:${certificate}`),
  issuerKeyHash: pseudoHash(`key:${certificate}`),
  serialNumber: pseudoHash(`serial:${certificate}`, 16),
});

export const sameHash = (a: any, b: any) =>
  a?.hashAlgorithm === b?.hashAlgorithm &&
  a?.issuerNameHash === b?.issuerNameHash &&
  a?.issuerKeyHash === b?.issuerKeyHash &&
  a?.serialNumber === b?.serialNumber;

/** A fake PEM CSR, enough for a CSMS that only relays it. */
export const fakeCsr = (chargePointId: string) =>
  `-----BEGIN CERTIFICATE REQUEST-----\n${btoaSafe(`CN=${chargePointId},O=EVsafe Simulator`)}\n-----END CERTIFICATE REQUEST-----`;

const btoaSafe = (value: string) => {
  const g: any = globalThis as any;
  if (typeof g.btoa === "function") return g.btoa(value);
  return g.Buffer.from(value).toString("base64");
};

// ---- diagnostics / log uploads ----------------------------------------------------------------

export interface UploadStatuses {
  uploading: string;
  uploaded: string;
  failed: (failure: TransferFailure) => string;
}

/** OCPP 1.6 DiagnosticsStatus. */
export const DIAGNOSTICS_STATUSES: UploadStatuses = { uploading: "Uploading", uploaded: "Uploaded", failed: () => "UploadFailed" };
/** UploadLogStatus (1.6 Security extension and 2.x): the failure says why. */
export const LOG_STATUSES: UploadStatuses = {
  uploading: "Uploading",
  uploaded: "Uploaded",
  failed: failure => (failure === "Failed" ? "UploadFailure" : failure),
};

export interface UploadRequest {
  action: string;
  location: string;
  retries?: number;
  retryInterval?: number;
  /** Log period (GetDiagnostics startTime/stopTime, GetLog oldest/latestTimestamp). */
  from?: string;
  to?: string;
  logType: "DiagnosticsLog" | "SecurityLog" | "DataCollectorLog";
  statuses: UploadStatuses;
  send: (status: string) => Promise<unknown>;
}

const DEFAULT_RETRIES = 1;
const DEFAULT_RETRY_INTERVAL_S = 30;

const compactTime = (date: Date) => date.toISOString().replace(/[-:]/g, "").replace(/\.\d+Z$/, "Z");

/** Hide the password of a location before it goes into a log. */
export const redact = (location: string) => location.replace(/\/\/([^:/@]+):[^@/]*@/, "//$1:***@");

/** The file a real charger would upload: identity, connector state, configuration and its log. */
export const diagnosticsFile = (
  sim: ChargePointSimulator,
  request: Pick<UploadRequest, "from" | "to" | "logType">
): TransferFile => {
  const now = new Date(sim.timestamp());
  const template = sim.settings.simulation.diagnosticFileName?.trim() || "{id}_{type}_{time}.log";
  const name = template
    .replace(/\{id\}/g, sim.settings.chargePointId)
    .replace(/\{type\}/g, request.logType === "DiagnosticsLog" ? "diagnostics" : request.logType.replace(/Log$/, "").toLowerCase())
    .replace(/\{time\}/g, compactTime(now))
    .replace(/[\\/:*?"<>|\s]+/g, "_");
  const from = request.from ? new Date(request.from).getTime() : -Infinity;
  const to = request.to ? new Date(request.to).getTime() : Infinity;
  const boot = sim.settings.bootNotification;
  const lines = [
    `# ${request.logType} of charge point ${sim.settings.chargePointId}`,
    `# Vendor: ${boot.chargePointVendor}  Model: ${boot.chargePointModel}  Serial: ${boot.chargePointSerialNumber ?? "-"}`,
    `# Firmware: ${boot.firmwareVersion ?? "-"}  Meter: ${boot.meterType ?? "-"} ${boot.meterSerialNumber ?? ""}`.trimEnd(),
    `# Protocol: ${sim.protocol}  Connection: ${sim.connectionState}  Generated: ${now.toISOString()}`,
    `# Period: ${request.from ?? "start"} .. ${request.to ?? "now"}`,
    "",
    "[connectors]",
    ...sim.evses.map(
      evse =>
        `connector=${evse.id} status=${evse.status} operative=${evse.operative} energyWh=${Math.round(evse.meterWh)} powerW=${Math.round(evse.powerW)} soc=${Math.round(evse.soc)}` +
        (evse.transaction ? ` transaction=${evse.transaction.id} idTag=${evse.transaction.idTag ?? ""}` : "")
    ),
    "",
    "[configuration]",
    ...sim.config
      .all()
      .filter(entry => entry.mutability !== "WriteOnly")
      .map(entry => `${entry.key ?? [entry.component.name, entry.variable.name, entry.variable.instance].filter(Boolean).join(".")}=${entry.value}`),
    "",
    "[log]",
    ...sim.history
      .filter(entry => {
        const time = entry.time.getTime();
        if (time < from || time > to) return false;
        // The security log holds connection and error events, not the OCPP traffic.
        return request.logType !== "SecurityLog" || entry.type === "socket" || entry.type === "error";
      })
      .map(entry => `${entry.time.toISOString()} ${entry.type.toUpperCase().padEnd(7)} ${(entry.command ?? "-").padEnd(28)} ${entry.message}`),
    "",
  ];
  return { name, content: lines.join("\n"), contentType: "text/plain" };
};

/**
 * Upload a diagnostics / log file to `location` for real, with the version's status
 * notifications: Uploading, then Uploaded or the failure status. A new request cancels the one in
 * progress (`cancelledPrevious`, which 2.x answers as AcceptedCanceled).
 */
export const startUpload = (sim: ChargePointSimulator, request: UploadRequest) => {
  const cancelledPrevious = Boolean(sim.activeUpload);
  if (sim.activeUpload) sim.activeUpload.cancelled = true;
  const job = { cancelled: false };
  sim.activeUpload = job;
  const file = diagnosticsFile(sim, request);

  sim.later(async () => {
    try {
      await request.send(request.statuses.uploading).catch(() => undefined);
      const attempts = 1 + Math.max(0, request.retries ?? DEFAULT_RETRIES);
      const retryMs = Math.max(0, request.retryInterval ?? DEFAULT_RETRY_INTERVAL_S) * 1000;
      let failure: TransferFailure = "Failed";
      for (let attempt = 1; attempt <= attempts && !job.cancelled; attempt++) {
        // Optional extra delay from the simulation settings, e.g. to watch Uploading in the CSMS.
        for (let left = Math.round(Number(sim.settings.simulation.diagnosticUploadTime) || 0); left > 0 && !job.cancelled; left--) {
          sim.setTransfer({ kind: "upload", label: file.name, secondsLeft: left });
          await sim.delay(1000);
        }
        if (job.cancelled) return;
        sim.setTransfer({ kind: "upload", label: file.name, secondsLeft: 0 });
        try {
          if (sim.settings.simulation.diagnosticStatus === "UploadFailed") {
            throw new TransferError("Failed", "upload failure forced by the simulation settings");
          }
          await sim.transfer.upload(request.location, file);
          if (job.cancelled) return;
          sim.log("info", `Uploaded ${file.name} (${file.content.length} bytes) to ${redact(request.location)}`, request.action);
          await request.send(request.statuses.uploaded).catch(() => undefined);
          return;
        } catch (error: any) {
          failure = error instanceof TransferError ? error.failure : "Failed";
          sim.log("error", `Upload attempt ${attempt}/${attempts} of ${file.name} failed: ${error?.message ?? error}`, request.action);
          // A bad URL, an unsupported protocol or a refused login will not get better by retrying.
          if (failure !== "Failed") break;
          if (attempt < attempts) await sim.delay(retryMs);
        }
      }
      if (!job.cancelled) await request.send(request.statuses.failed(failure)).catch(() => undefined);
    } finally {
      if (sim.activeUpload === job) {
        sim.activeUpload = null;
        sim.setTransfer(null);
      }
    }
  });
  return { fileName: file.name, cancelledPrevious };
};

// ---- firmware ---------------------------------------------------------------------------------

export interface FirmwareRequest {
  action: string;
  location: string;
  retrieveAt?: string;
  installAt?: string;
  retries?: number;
  retryInterval?: number;
  /** A signature came with the firmware: report SignatureVerified (or InvalidSignature). */
  signed: boolean;
  /** The version's FirmwareStatus has DownloadScheduled / InstallScheduled / InstallRebooting. */
  extendedStatuses: boolean;
  send: (status: string) => Promise<unknown>;
}

/** The firmware version in a file name, e.g. `evsafe-ac-2.4.1.bin` -> `2.4.1`. */
export const firmwareVersionFrom = (location: string) => {
  const file = decodeURIComponent(location.split(/[?#]/)[0].split("/").pop() ?? "");
  return /\d+(?:\.\d+)+(?:[-+][\w.]+)?/.exec(file.replace(/\.(bin|img|tar|gz|tgz|zip|hex|fw)$/gi, ""))?.[0];
};

class Cancelled extends Error {}

/**
 * A firmware update: wait for retrieveDate, download `location` for real (with retries), verify,
 * wait for running transactions to end, install, reboot, and report Installed after the boot.
 * The simulation setting `firmWareStatus` can force a failure at a step.
 */
export const startFirmwareUpdate = (sim: ChargePointSimulator, request: FirmwareRequest) => {
  const cancelledPrevious = Boolean(sim.activeFirmware);
  if (sim.activeFirmware) sim.activeFirmware.cancelled = true;
  const job = { cancelled: false };
  sim.activeFirmware = job;
  const forced = sim.settings.simulation.firmWareStatus;
  const name = decodeURIComponent(request.location.split(/[?#]/)[0].split("/").pop() || "firmware");

  const status = async (value: string) => {
    if (job.cancelled) throw new Cancelled();
    sim.records.firmwareStatus = value;
    await request.send(value).catch(() => undefined);
  };
  const waitUntil = async (time: string | undefined, scheduledStatus: string) => {
    const ms = time ? new Date(time).getTime() - new Date(sim.timestamp()).getTime() : 0;
    if (!(ms > 0)) return;
    if (request.extendedStatuses) await status(scheduledStatus);
    sim.log("info", `${request.action}: waiting until ${time}`, request.action);
    await sim.delay(ms);
  };

  sim.later(async () => {
    try {
      await waitUntil(request.retrieveAt, "DownloadScheduled");
      await status("Downloading");
      const attempts = 1 + Math.max(0, request.retries ?? DEFAULT_RETRIES);
      let firmware: Uint8Array | null = null;
      for (let attempt = 1; attempt <= attempts && !firmware; attempt++) {
        if (job.cancelled) return;
        sim.setTransfer({ kind: "download", label: name, secondsLeft: 0 });
        try {
          if (forced === "DownloadFailed") throw new TransferError("Failed", "download failure forced by the simulation settings");
          firmware = await sim.transfer.download(request.location);
        } catch (error: any) {
          sim.log("error", `Firmware download attempt ${attempt}/${attempts} failed: ${error?.message ?? error}`, request.action);
          if (attempt < attempts) await sim.delay(Math.max(0, request.retryInterval ?? DEFAULT_RETRY_INTERVAL_S) * 1000);
        }
      }
      sim.setTransfer(null);
      if (!firmware) return await status("DownloadFailed");
      sim.log("info", `Downloaded ${name} (${firmware.length} bytes, md5 ${md5Hex(firmware)})`, request.action);
      await status("Downloaded");

      if (request.signed) {
        if (forced === "InvalidSignature") return await status("InvalidSignature");
        await status("SignatureVerified");
      }
      await waitUntil(request.installAt, "InstallScheduled");
      if (sim.evses.some(evse => evse.transaction)) {
        sim.log("info", "Firmware installation waits for the running transactions to end", request.action);
        while (sim.evses.some(evse => evse.transaction)) {
          if (job.cancelled) return;
          await sim.delay(1000);
        }
      }
      await status("Installing");
      await sim.delay(sim.stepDelayMs);
      if (forced === "InstallationFailed") return await status("InstallationFailed");

      const version = firmwareVersionFrom(request.location);
      if (version) sim.settings.bootNotification.firmwareVersion = version;
      if (request.extendedStatuses) await status("InstallRebooting");
      sim.onNextBoot(() => status("Installed").catch(() => undefined));
      sim.log("info", `Firmware ${version ?? name} installed, rebooting`, request.action);
      sim.reboot("FirmwareUpdate");
    } catch (error) {
      if (!(error instanceof Cancelled)) throw error;
    } finally {
      if (sim.activeFirmware === job) sim.activeFirmware = null;
      sim.setTransfer(null);
    }
  });
  return { cancelledPrevious };
};

/** 2.x PublishFirmware (local controller): download, check the MD5 checksum, publish. */
export const startPublishFirmware = (
  sim: ChargePointSimulator,
  request: { location: string; checksum: string; retries?: number; retryInterval?: number; send: (status: string, extra?: any) => Promise<unknown> }
) => {
  sim.later(async () => {
    const send = (value: string, extra?: any) => request.send(value, extra).catch(() => undefined);
    await send("Downloading");
    const attempts = 1 + Math.max(0, request.retries ?? DEFAULT_RETRIES);
    let firmware: Uint8Array | null = null;
    for (let attempt = 1; attempt <= attempts && !firmware; attempt++) {
      try {
        firmware = await sim.transfer.download(request.location);
      } catch (error: any) {
        sim.log("error", `PublishFirmware download attempt ${attempt}/${attempts} failed: ${error?.message ?? error}`, "PublishFirmware");
        if (attempt < attempts) await sim.delay(Math.max(0, request.retryInterval ?? DEFAULT_RETRY_INTERVAL_S) * 1000);
      }
    }
    if (!firmware) return void (await send("DownloadFailed"));
    await send("Downloaded");
    const checksum = md5Hex(firmware);
    if (checksum.toLowerCase() !== request.checksum.toLowerCase()) {
      sim.log("error", `PublishFirmware checksum ${checksum} does not match ${request.checksum}`, "PublishFirmware");
      return void (await send("InvalidChecksum"));
    }
    await send("ChecksumVerified");
    sim.publishedFirmware = { checksum: request.checksum, location: request.location };
    await send("Published", { location: [request.location] });
  });
};
